import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Package, ChevronRight, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusClass = (status) => {
  const statusMap = {
    'Solicitud Abierta': 'status-open',
    'En Tránsito al Servicio Técnico': 'status-transit',
    'Entregado en Servicio Técnico': 'status-transit',
    'Haciendo Comprobaciones': 'status-checking',
    'En Reparación': 'status-repairing',
    'Reparado': 'status-repaired',
    'Sustituido / Reemplazado': 'status-replaced',
    'Enviado al Cliente': 'status-shipped',
    'RMA Completado': 'status-completed'
  };
  return statusMap[status] || 'status-open';
};

const WarrantyList = () => {
  const { t } = useTranslation();
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWarranties = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/warranty`, { withCredentials: true });
      setWarranties(response.data);
    } catch (error) {
      console.error('Error fetching warranties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarranties();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="warranty-list-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#0A0A0A] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchWarranties}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{t('dashboard.warranty.check')}</h1>
          <Link to="/warranty/new">
            <Button className="bg-[#0047FF] hover:bg-[#003BCC]" data-testid="new-warranty-btn">
              {t('dashboard.warranty.new')}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
          </div>
        ) : warranties.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center">
            <Package className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
            <p className="text-[#52525B]">No tienes solicitudes de garantía</p>
            <Link to="/warranty/new">
              <Button className="mt-4 bg-[#0047FF] hover:bg-[#003BCC]">
                {t('dashboard.warranty.new')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {warranties.map((warranty, index) => (
              <motion.div
                key={warranty.request_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/warranty/${warranty.request_id}`} data-testid={`warranty-item-${index}`}>
                  <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm text-[#52525B]">
                            #{warranty.request_id?.slice(0, 12).toUpperCase()}
                          </span>
                          <span className={`status-badge ${getStatusClass(warranty.status)}`}>
                            {warranty.status}
                          </span>
                        </div>
                        <h3 className="font-semibold text-lg mb-1">{warranty.product_name}</h3>
                        <p className="text-sm text-[#52525B]">
                          {warranty.purchase_source} • Pedido: {warranty.order_id}
                        </p>
                        <p className="text-xs text-[#A1A1AA] mt-2">
                          {new Date(warranty.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#A1A1AA]" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default WarrantyList;
